import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  LS_QUIZ_AUDIO_KEYS,
  QUIZ_AUDIO_PREFS_EVENT,
  readQuizAudioPrefs,
} from '@/utils/quizAudioPrefs';
import { unlockGlobalQuizBgm } from '@/utils/globalQuizBgm';

/** Chuông 2 harmonic — hiệu ứng SFX */
function bellTone(ctx, t0, freq, duration, vol, dest) {
  const o1 = ctx.createOscillator();
  const o2 = ctx.createOscillator();
  const g1 = ctx.createGain();
  const g2 = ctx.createGain();
  const m = ctx.createGain();
  o1.type = 'sine';
  o2.type = 'sine';
  o1.frequency.setValueAtTime(freq, t0);
  o2.frequency.setValueAtTime(freq * 2.01, t0);
  g2.gain.value = 0.2;
  o1.connect(g1);
  o2.connect(g2);
  g1.connect(m);
  g2.connect(m);
  m.connect(dest);
  const atk = Math.min(0.05, duration * 0.12);
  m.gain.setValueAtTime(0.0001, t0);
  m.gain.linearRampToValueAtTime(vol, t0 + atk);
  m.gain.exponentialRampToValueAtTime(vol * 0.55, t0 + duration * 0.4);
  m.gain.exponentialRampToValueAtTime(0.0001, t0 + duration);
  o1.start(t0);
  o2.start(t0);
  o1.stop(t0 + duration + 0.06);
  o2.stop(t0 + duration + 0.06);
}

function connectSfxBus(ctx) {
  const bus = ctx.createGain();
  bus.gain.value = 1;
  bus.connect(ctx.destination);
  return bus;
}

/**
 * SFX: Web Audio. Nhạc nền: singleton toàn app (`globalQuizBgm.js` + `App.jsx`).
 * Cài đặt: popover /settings. Mở phát sau gesture (App lắng nghe) hoặc khi có SFX.
 */
export function useQuizAudio() {
  const [prefs, setPrefs] = useState(() => readQuizAudioPrefs());
  const ctxRef = useRef(null);
  const sfxBusRef = useRef(null);
  const unlockedRef = useRef(false);
  const prefsRef = useRef(prefs);
  prefsRef.current = prefs;

  const ensureCtx = useCallback(() => {
    if (typeof window === 'undefined') return null;
    if (!ctxRef.current) {
      const Ctx = window.AudioContext || window.webkitAudioContext;
      if (!Ctx) return null;
      ctxRef.current = new Ctx();
      sfxBusRef.current = connectSfxBus(ctxRef.current);
    }
    return ctxRef.current;
  }, []);

  const getSfxBus = useCallback(() => {
    const ctx = ensureCtx();
    if (!ctx) return null;
    if (!sfxBusRef.current || sfxBusRef.current.context !== ctx) {
      sfxBusRef.current = connectSfxBus(ctx);
    }
    return sfxBusRef.current;
  }, [ensureCtx]);

  const unlockAudio = useCallback(async () => {
    const ctx = ensureCtx();
    if (ctx) {
      try {
        if (ctx.state === 'suspended') await ctx.resume();
      } catch {
        /* nhạc nền HTML vẫn có thể phát */
      }
    }
    unlockedRef.current = true;
    unlockGlobalQuizBgm();
  }, [ensureCtx]);

  const syncPrefs = useCallback(() => {
    setPrefs(readQuizAudioPrefs());
  }, []);

  useEffect(() => {
    const onEvt = () => syncPrefs();
    const onStorage = (e) => {
      if (e.key && LS_QUIZ_AUDIO_KEYS.has(e.key)) syncPrefs();
    };
    window.addEventListener(QUIZ_AUDIO_PREFS_EVENT, onEvt);
    window.addEventListener('storage', onStorage);
    return () => {
      window.removeEventListener(QUIZ_AUDIO_PREFS_EVENT, onEvt);
      window.removeEventListener('storage', onStorage);
    };
  }, [syncPrefs]);

  useEffect(
    () => () => {
      try {
        sfxBusRef.current?.disconnect();
      } catch {
        /* ignore */
      }
      sfxBusRef.current = null;
      try {
        ctxRef.current?.close();
      } catch {
        /* ignore */
      }
      ctxRef.current = null;
      unlockedRef.current = false;
    },
    [],
  );

  const runSfx = useCallback(
    (playFn) => {
      if (!prefsRef.current.sfxOn) return;
      const ctx = ensureCtx();
      if (!ctx) return;
      const dest = getSfxBus();
      if (!dest) return;
      const v = prefsRef.current.sfxVol / 100;
      const run = () => {
        if (ctx.state !== 'running') return;
        playFn(ctx, ctx.currentTime, dest, v);
        unlockGlobalQuizBgm();
      };
      const afterResume = () => {
        unlockedRef.current = true;
        unlockGlobalQuizBgm();
        run();
      };
      if (ctx.state === 'suspended') {
        ctx.resume().then(afterResume).catch(() => {});
      } else {
        unlockedRef.current = true;
        afterResume();
      }
    },
    [ensureCtx, getSfxBus]
  );

  const playNav = useCallback(() => {
    runSfx((ctx, t, dest, vm) => {
      const vol = 0.11 * vm;
      const seq = [261.63, 329.63, 392.0, 523.25];
      let tt = t;
      seq.forEach((f) => {
        bellTone(ctx, tt, f, 0.42, vol, dest);
        tt += 0.28;
      });
    });
  }, [runSfx]);

  const playSelect = useCallback(() => {
    runSfx((ctx, t, dest, vm) => {
      const vol = 0.12 * vm;
      bellTone(ctx, t, 659.25, 0.35, vol * 0.9, dest);
      bellTone(ctx, t + 0.22, 783.99, 0.4, vol * 0.75, dest);
      bellTone(ctx, t + 0.48, 1046.5, 0.45, vol * 0.55, dest);
    });
  }, [runSfx]);

  const playSubmitClick = useCallback(() => {
    runSfx((ctx, t, dest, vm) => {
      const vol = 0.1 * vm;
      const up = [392, 493.88, 587.33, 659.25, 783.99];
      let tt = t;
      up.forEach((f, i) => {
        bellTone(ctx, tt, f, 0.32 - i * 0.02, vol, dest);
        tt += 0.24;
      });
      bellTone(ctx, tt + 0.05, 523.25, 0.55, vol * 0.85, dest);
    });
  }, [runSfx]);

  const playSubmitError = useCallback(() => {
    runSfx((ctx, t, dest, vm) => {
      const vol = 0.09 * vm;
      const down = [349.23, 311.13, 277.18, 246.94, 220.0];
      let tt = t;
      down.forEach((f, i) => {
        bellTone(ctx, tt, f, 0.38 + i * 0.05, vol, dest);
        tt += 0.32;
      });
    });
  }, [runSfx]);

  const playTimerUrgent = useCallback(() => {
    runSfx((ctx, t, dest, vm) => {
      const vol = 0.085 * vm;
      for (let i = 0; i < 4; i += 1) {
        bellTone(ctx, t + i * 0.22, i % 2 === 0 ? 523.25 : 440.0, 0.28, vol, dest);
      }
    });
  }, [runSfx]);

  const playResultPass = useCallback(() => {
    runSfx((ctx, t, dest, vm) => {
      const vol = 0.1 * vm;
      const win = [392.0, 493.88, 587.33, 659.25, 783.99, 880.0, 987.77, 1046.5];
      let tt = t;
      win.forEach((f, i) => {
        bellTone(ctx, tt, f, 0.36, vol * (0.75 + i * 0.03), dest);
        tt += 0.22;
      });
      bellTone(ctx, tt + 0.08, 523.25, 0.9, vol * 0.7, dest);
      bellTone(ctx, tt + 0.12, 659.25, 0.95, vol * 0.55, dest);
    });
  }, [runSfx]);

  const playResultFail = useCallback(() => {
    runSfx((ctx, t, dest, vm) => {
      const vol = 0.09 * vm;
      const lose = [392.0, 369.99, 349.23, 329.63, 311.13, 293.66, 277.18, 261.63];
      let tt = t;
      lose.forEach((f, i) => {
        bellTone(ctx, tt, f, 0.42 + i * 0.04, vol, dest);
        tt += 0.26;
      });
    });
  }, [runSfx]);

  return useMemo(
    () => ({
      unlockAudio,
      playNav,
      playSelect,
      playSubmitClick,
      playSubmitError,
      playTimerUrgent,
      playResultPass,
      playResultFail,
    }),
    [
      unlockAudio,
      playNav,
      playSelect,
      playSubmitClick,
      playSubmitError,
      playTimerUrgent,
      playResultPass,
      playResultFail,
    ]
  );
}
